import { Box, Stack, Text, useToast } from '@chakra-ui/react';
import { IconButton } from '@chakra-ui/react';
import { Document } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL, getPrettyDocumentType } from '@pelicargo/utils';
import { useMemo, useState } from 'react';
import {
  HiCheck,
  HiCheckCircle,
  HiOutlineArrowDownTray,
  HiOutlineCheckCircle,
  HiOutlineDocument,
} from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';

type Props = {
  document: Partial<Document>;
};

export const DocumentRow = ({ document }: Props) => {
  const { mutateAsync: downloadDocument, isLoading } = trpc.downloadDocument.useMutation();
  const [isHovering, setIsHovering] = useState(false);
  const { request } = useCurrentRequest();

  const toast = useToast({ position: 'bottom', duration: 4000 });

  const isUploaded = useMemo(() => !!document?.id, [document?.id]);
  const isRequestOwnedByDocumentAccount = useMemo(
    () => request?.account_id && document?.account_id && request.account_id === document.account_id,
    [request?.account_id, document?.account_id],
  );
  const requiresApproval = useMemo(
    () => !isRequestOwnedByDocumentAccount && DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(document.type),
    [document.type, isRequestOwnedByDocumentAccount],
  );
  const isApproved = useMemo(() => !!document?.approved_at, [document?.approved_at]);

  const handleDownload = async (id: number) => {
    try {
      const { signedUrl } = await downloadDocument({ id });
      window.open(signedUrl, '_blank');
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  return (
    <Stack
      key={document?.id || document?.type}
      direction="row"
      justify="space-between"
      align="center"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Stack direction="row" align="center" spacing="2" minW="150px">
        <Show if={isUploaded && !requiresApproval}>
          <Box>
            <HiCheck color="green" title="Approval not required" />
          </Box>
        </Show>
        <Show if={isUploaded && requiresApproval && !isApproved}>
          <Box>
            <HiOutlineCheckCircle color="green" title="Awaiting approval" />
          </Box>
        </Show>
        <Show if={isUploaded && requiresApproval && isApproved}>
          <Box>
            <HiCheckCircle color="green" title="Approved" />
          </Box>
        </Show>
        <Show if={!isUploaded}>
          <Box>
            <HiOutlineDocument title="Not uploaded" />
          </Box>
        </Show>
        <Text fontWeight="semibold" whiteSpace="nowrap">
          {getPrettyDocumentType(document?.type)}
        </Text>
        <Text title={document?.original_filename} color="text.subtle" noOfLines={1}>
          {document?.original_filename}
        </Text>
      </Stack>
      <Show if={isUploaded}>
        <IconButton
          icon={<HiOutlineArrowDownTray />}
          opacity={isHovering || isLoading ? 1 : 0}
          transition="all 0.3s"
          aria-label="Download Document"
          variant="solid"
          size="sm"
          isLoading={isLoading}
          onClick={() => handleDownload(document.id)}
        />
      </Show>
    </Stack>
  );
};
